import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  useScrollTrigger,
  Slide,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import MenuIcon from "@mui/icons-material/Menu";
import Logo from "../../assets/images/logotix.png";
import { useState, useEffect } from "react";

// Styled Components
const HeaderContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  width: "95%",
  maxWidth: "1400px",
  zIndex: 1000,
  padding: theme.spacing(1),
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  [theme.breakpoints.down("lg")]: {
    width: "98%",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: theme.spacing(0.5),
  },
}));

const GlassHeader = styled(Box)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(20px)",
  borderRadius: "20px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  padding: theme.spacing(1.5, 3),
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.98)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1, 2),
    borderRadius: "16px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75, 1.5),
    borderRadius: "12px",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  textDecoration: "none",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.02)",
  },
}));

const LogoImage = styled("img")(({ theme }) => ({
  height: "50px",
  width: "auto",
  transition: "all 0.3s ease",
  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
  [theme.breakpoints.down("md")]: {
    height: "45px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "40px",
  },
}));

const BrandText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.5rem",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  letterSpacing: "-0.5px",
  [theme.breakpoints.down("md")]: {
    fontSize: "1.3rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.1rem",
  },
}));

const NavButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: "8px 16px",
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.primary,
  transition: "all 0.3s ease",
  "&:hover": {
    background:
      "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.2)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "6px 12px",
    fontSize: "0.875rem",
  },
}));

const PremiumButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: "10px 20px",
  fontWeight: 600,
  textTransform: "none",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
    boxShadow: "0 8px 25px rgba(102, 126, 234, 0.6)",
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 16px",
    fontSize: "0.875rem",
  },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: "none",
  borderRadius: "12px",
  padding: "8px",
  color: theme.palette.text.primary,
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(102, 126, 234, 0.1)",
    transform: "scale(1.05)",
  },
  [theme.breakpoints.down("md")]: {
    display: "flex",
  },
}));

export default function HeaderPub() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <HeaderContainer>
      <Fade in timeout={800}>
        <GlassHeader
          sx={{
            transform: scrolled ? "scale(0.98)" : "scale(1)",
            background: scrolled
              ? "rgba(255, 255, 255, 0.98)"
              : "rgba(255, 255, 255, 0.95)",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box
              component={RouterLink}
              to="/"
              sx={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
            >
              <LogoImage src={Logo} alt="SODTix Logo" />
              <BrandText
                variant="h6"
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                SODTix
              </BrandText>
            </Box>

            {/* Navigation Section - Desktop */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              <NavButton startIcon={<HomeIcon />} onClick={() => navigate("/")}>
                Home
              </NavButton>

              <NavButton
                startIcon={<ConfirmationNumberIcon />}
                onClick={() => navigate("/")}
              >
                Events
              </NavButton>

              <PremiumButton
                variant="contained"
                onClick={() => navigate("/")}
                sx={{ ml: 1 }}
              >
                Get Tickets
              </PremiumButton>
            </Stack>

            {/* Mobile Navigation */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ display: { xs: "flex", md: "none" } }}
            >
              <NavButton
                size="small"
                startIcon={<HomeIcon sx={{ fontSize: "1rem" }} />}
                onClick={() => navigate("/")}
                sx={{ minWidth: "auto", px: 1 }}
              >
                <Typography sx={{ display: { xs: "none", sm: "block" } }}>
                  Home
                </Typography>
              </NavButton>

              <PremiumButton
                size="small"
                variant="contained"
                onClick={() => navigate("/")}
              >
                Tickets
              </PremiumButton>

              <MobileMenuButton>
                <MenuIcon />
              </MobileMenuButton>
            </Stack>
          </Stack>
        </GlassHeader>
      </Fade>
    </HeaderContainer>
  );
}
